import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import crmService from '../../services/crmService';
import { sanitizeMuLocal, toMuPhone, fromMuPhone, isCompleteMuLocal } from '../../lib/muPhone';
import { useAppSettings } from '../../context/AppSettingsContext';
import { HeartHandshake, Search, ChevronRight, Ban } from 'lucide-react';
import { useT } from '../../context/LanguageContext';

/*
 * CRM — Customer 360: guest profiles built from order history (phone = unique
 * key). The five growth reports (RFM segments, top customers, retention, menu
 * magnets, win-back) live in the Reports section (and the Customers sidebar
 * group); guest rows there deep-link back here with ?customer=<id>.
 */

// Booking source + status → badge label / colour.
const SOURCE_LABEL = { ONLINE: 'Website', PHONE: 'Phone', RECEPTION: 'Reception' };
const SOURCE_CLS = {
  ONLINE: 'bg-blue-50 text-blue-700',
  PHONE: 'bg-violet-50 text-violet-700',
  RECEPTION: 'bg-slate-100 text-slate-700',
};
const BOOKING_STATUS_CLS = {
  BOOKED: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-emerald-100 text-emerald-800',
  SEATED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-slate-100 text-slate-600',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-red-50 text-red-500',
};

export default function CrmPage() {
  const { t } = useT();
  // The blacklist switch asks once, inline, before it does anything.
  const [blAsk, setBlAsk] = useState(false);
  const [blBusy, setBlBusy] = useState(false);
  const { formatCurrency, formatDate } = useAppSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [phone, setPhone] = useState('');
  const [profile, setProfile] = useState(null);
  const [profileErr, setProfileErr] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const money = (n) => formatCurrency(Number(n) || 0);
  const dt = (iso) => (iso ? formatDate(iso) : '—');

  const lookup = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) return;
    if (!isCompleteMuLocal(phone)) {
      setProfileErr("Enter the guest's 8-digit mobile number.");
      return;
    }
    setProfileLoading(true);
    setProfileErr('');
    setProfile(null);
    try {
      setProfile(await crmService.profileByPhone(toMuPhone(phone)));
    } catch (err) {
      setProfileErr(err.response?.data?.error || 'Lookup failed.');
    } finally {
      setProfileLoading(false);
    }
  };

  // Deep link from the CRM reports under /reports — a guest row navigates to
  // /crm?customer=<id>; open that profile straight away.
  const customerParam = searchParams.get('customer');
  useEffect(() => {
    if (!customerParam) return;
    (async () => {
      setProfileLoading(true);
      setProfileErr('');
      setProfile(null);
      try {
        const data = await crmService.profileById(customerParam);
        setPhone(fromMuPhone(data.customer?.phone));
        setProfile(data);
      } catch (err) {
        setProfileErr(err.response?.data?.error || 'Lookup failed.');
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [customerParam]);

  // Arrived from another screen — offer the way back to the right one.
  const BACK_FROM = {
    reservations: { to: '/reservations/all', label: 'Back to All Reservations' },
    // Kept for links saved before requests moved into All Reservations.
    'reservation-requests': { to: '/reservations/all?status=BOOKED', label: 'Back to the requests' },
    customers: { to: '/customers', label: 'Back to Customers' },
    // A name on the Tables screen's order sheet opens a guest card, whose
    // "Open full CRM profile" lands here.
    tables: { to: '/tables', label: 'Back to Tables' },
  };
  const backTo = BACK_FROM[searchParams.get('from')];

  return (
    <div className="p-6 w-full">
      {backTo && (
        <button
          type="button"
          onClick={() => navigate(backTo.to)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <ChevronRight size={15} className="rotate-180" /> {backTo.label}
        </button>
      )}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
          <HeartHandshake className="h-7 w-7 text-amber-600" /> CRM
        </h1>
        <p className="text-gray-600 mt-1">
          Guest profiles built from their orders — the phone number is each guest's unique key.
        </p>
      </div>

      <div className="space-y-4">
        <Card className="p-4">
          <form onSubmit={lookup} className="flex gap-2 max-w-md">
            <div className="flex-1">
              <Input placeholder="XXXX XXXX" maxLength={8} inputMode="numeric"
                value={phone} onChange={(e) => setPhone(sanitizeMuLocal(e.target.value))} />
            </div>
            <Button type="submit" disabled={profileLoading || !phone.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
              <Search size={15} className="mr-1" /> Look up
            </Button>
          </form>
          {profileErr && <p className="text-sm text-red-600 mt-2">{profileErr}</p>}
          {profile?.customer?.id && (
            <button type="button"
              onClick={() => navigate(`/customers/${profile.customer.id}${searchParams.get('from') ? `?from=${searchParams.get('from')}` : ''}`)}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:underline underline-offset-2">
              {t('crm.p.openFull')} <ChevronRight size={15} />
            </button>
          )}
        </Card>

        {profileLoading && <Card className="p-10 text-center text-gray-500">Loading profile…</Card>}

        {profile && (
          <>
            {/* Identity + stat tiles */}
            <Card className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{profile.customer.full_name}</h2>
                  {/* Placeholder addresses (OTP/counter-created guests) are
                      bookkeeping, not contact info — the phone stands alone. */}
                  <p className="text-sm text-gray-500">
                    {profile.customer.phone}
                    {profile.customer.email && !profile.customer.email_is_placeholder
                      ? ` · ${profile.customer.email}` : ''}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  profile.customer.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>{profile.customer.is_active ? 'Active' : 'Inactive'}</span>
                {profile.customer.is_blacklisted && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-800 text-white"
                    title={profile.customer.blacklist_reason || ''}>
                    <Ban size={12} /> {t('crm.blacklist.badge')}
                    {profile.customer.blacklisted_at ? ` · ${t('crm.blacklist.since', { when: dt(profile.customer.blacklisted_at) })}` : ''}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {[
                  ['Visits', profile.stats.visit_count],
                  ['Total spend', money(profile.stats.total_spend)],
                  ['Avg order', money(profile.stats.avg_order_value)],
                  ['Usual party', profile.stats.avg_party_size ? profile.stats.avg_party_size.toFixed(1) : '—'],
                  ['First visit', dt(profile.stats.first_visit)],
                  ['Last visit', dt(profile.stats.last_visit)],
                  ['Days since last', profile.stats.days_since_last_visit ?? '—'],
                  ['Visits every', profile.stats.median_days_between_visits
                    ? `~${profile.stats.median_days_between_visits} days` : '—'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">{label}</div>
                    <div className="text-lg font-semibold text-gray-900">{value}</div>
                  </div>
                ))}
                {/* Booking advance the restaurant holds for this guest — paid
                    with a reservation, not yet consumed by any bill. It is
                    deducted automatically when their sitting is settled. */}
                {(profile.stats.advance_available || 0) > 0 && (
                  <div className="rounded-lg bg-emerald-50 p-3">
                    <div className="text-xs text-emerald-600">Booking advance held</div>
                    <div className="text-lg font-semibold text-emerald-700">
                      {money(profile.stats.advance_available)}
                    </div>
                    <div className="text-[10px] text-emerald-600 mt-0.5">
                      deducted from their next bill
                    </div>
                  </div>
                )}
                {/* Booking reliability — staff-facing judgement, red once it
                    is a pattern. Each underlying no-show is a staff decision
                    (there is no auto-void) and is visible per booking below. */}
                <div className={`rounded-lg p-3 ${
                  (profile.customer.no_show_count || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <div className={`text-xs ${
                    (profile.customer.no_show_count || 0) > 0 ? 'text-red-600' : 'text-gray-500'
                  }`}>No-shows</div>
                  <div className={`text-lg font-semibold ${
                    (profile.customer.no_show_count || 0) > 0 ? 'text-red-700' : 'text-gray-900'
                  }`}>
                    {profile.customer.no_show_count || 0}
                  </div>
                  {profile.customer.last_no_show_at && (
                    <div className="text-[10px] text-red-500 mt-0.5">
                      last {dt(profile.customer.last_no_show_at)}
                    </div>
                  )}
                </div>
                {/* Tables held on the clock and let go unpaid — each one blocked
                    a table for nobody. Counted by the sweep, not by staff, and
                    a statistic only: the blacklist is made of no-shows, so
                    these are here to be read, not to refuse anybody. */}
                <div className={`rounded-lg p-3 ${
                  (profile.customer.hold_lapse_count || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <div className={`text-xs ${
                    (profile.customer.hold_lapse_count || 0) > 0 ? 'text-red-600' : 'text-gray-500'
                  }`} title={t('crm.holds.hint')}>{t('crm.holds.title')}</div>
                  <div className={`text-lg font-semibold ${
                    (profile.customer.hold_lapse_count || 0) > 0 ? 'text-red-700' : 'text-gray-900'
                  }`}>
                    {profile.customer.hold_lapse_count || 0}
                  </div>
                  {profile.customer.last_hold_lapse_at && (
                    <div className="text-[10px] text-red-500 mt-0.5">
                      {t('crm.holds.last', { when: dt(profile.customer.last_hold_lapse_at) })}
                    </div>
                  )}
                </div>
                {/* The switch itself, asked once inline. Lifting zeroes the
                    no-show strikes, or the next one would put the guest
                    straight back; the lifetime no-show count above stays. */}
                <div className={`rounded-lg p-3 ${profile.customer.is_blacklisted ? 'bg-slate-800 text-white' : 'bg-gray-50'}`}>
                  <div className={`text-xs ${profile.customer.is_blacklisted ? 'text-slate-300' : 'text-gray-500'}`}>
                    {profile.customer.is_blacklisted ? t('crm.blacklist.refused') : t('crm.blacklist.putHint')}
                  </div>
                  {!blAsk ? (
                    <button type="button" disabled={blBusy}
                      onClick={() => setBlAsk(true)}
                      className={`mt-1.5 text-sm font-semibold underline-offset-2 hover:underline ${
                        profile.customer.is_blacklisted ? 'text-white' : 'text-red-700'}`}>
                      {profile.customer.is_blacklisted ? t('crm.blacklist.lift') : t('crm.blacklist.put')}
                    </button>
                  ) : (
                    <div className="mt-1.5 flex items-center gap-2 text-sm">
                      <span className={profile.customer.is_blacklisted ? 'text-slate-200' : 'text-gray-700'}>
                        {t('crm.blacklist.sure')}
                        {profile.customer.is_blacklisted ? ` ${t('crm.blacklist.liftHint')}` : ''}
                      </span>
                      <button type="button" disabled={blBusy}
                        onClick={async () => {
                          setBlBusy(true);
                          try {
                            const next = await crmService.setBlacklist(
                              profile.customer.id, !profile.customer.is_blacklisted);
                            setProfile(next);
                            setBlAsk(false);
                          } catch (e) {
                            setProfileErr(e?.message || t('crm.blacklist.failed'));
                          } finally { setBlBusy(false); }
                        }}
                        className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-red-700">
                        {t('crm.blacklist.yes')}
                      </button>
                      <button type="button" disabled={blBusy} onClick={() => setBlAsk(false)}
                        className={`text-xs underline-offset-2 hover:underline ${
                          profile.customer.is_blacklisted ? 'text-slate-200' : 'text-gray-600'}`}>
                        {t('crm.blacklist.no')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {profile.stats.favourite_location && (
                <p className="text-sm text-gray-500 mt-3">
                  Usually visits <strong>{profile.stats.favourite_location}</strong>
                </p>
              )}
            </Card>

            {/* Favourites */}
            <Card className="p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Favourite dishes</h3>
              {profile.favourites.length === 0 ? (
                <p className="text-sm text-gray-500">No completed orders yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-gray-500">
                      <tr>
                        <th className="text-left font-medium py-2 pr-4">Dish</th>
                        <th className="text-right font-medium py-2 pr-4">Qty</th>
                        <th className="text-right font-medium py-2 pr-4">Orders</th>
                        <th className="text-right font-medium py-2">Spend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {profile.favourites.map((f) => (
                        <tr key={f.item}>
                          <td className="py-2 pr-4 font-medium text-gray-900">{f.item}</td>
                          <td className="py-2 pr-4 text-right">{f.quantity}</td>
                          <td className="py-2 pr-4 text-right">{f.orders}</td>
                          <td className="py-2 text-right">{money(f.spend)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Bookings — every reservation, online or offline (requests too) */}
            <Card className="p-5">
              <h3 className="font-semibold text-gray-800 mb-3">
                Bookings
                {profile.bookings?.length ? (
                  <span className="ml-2 text-sm font-normal text-gray-400">
                    {profile.bookings.length} total
                  </span>
                ) : null}
              </h3>
              {!profile.bookings || profile.bookings.length === 0 ? (
                <p className="text-sm text-gray-500">No bookings yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-gray-500">
                      <tr>
                        <th className="text-left font-medium py-2 pr-4">When</th>
                        <th className="text-left font-medium py-2 pr-4">Source</th>
                        <th className="text-left font-medium py-2 pr-4">Party</th>
                        <th className="text-left font-medium py-2 pr-4">Table</th>
                        <th className="text-left font-medium py-2 pr-4">Type</th>
                        <th className="text-left font-medium py-2 pr-4">Status</th>
                        <th className="text-left font-medium py-2 pr-4">Payment</th>
                        <th className="text-left font-medium py-2">Ref</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {profile.bookings.map((b) => (
                        <tr key={b.id}>
                          <td className="py-2 pr-4 text-gray-900">{dt(b.reserved_start_at)}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              SOURCE_CLS[b.source] || 'bg-gray-100 text-gray-600'
                            }`}>{SOURCE_LABEL[b.source] || b.source}</span>
                          </td>
                          <td className="py-2 pr-4">{b.pax ?? '—'} pax</td>
                          <td className="py-2 pr-4">{b.table_code || '—'}</td>
                          <td className="py-2 pr-4">
                            {b.dining_type === 'BUFFET'
                              ? `Buffet${b.buffet_name ? ` · ${b.buffet_name}` : ''}`
                              : 'À la carte'}
                            {b.has_preorder && (
                              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700">PRE-ORDER</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              BOOKING_STATUS_CLS[b.status] || 'bg-gray-100 text-gray-600'
                            }`}>
                              {b.status === 'BOOKED' ? 'Awaiting approval' : b.status}
                            </span>
                            {/* The audit facts behind the badge: a no-show is a
                                staff decision, so say whose and when. */}
                            {b.status === 'NO_SHOW' && (b.no_show_marked_by || b.no_show_at) && (
                              <div className="text-[10px] text-gray-400 mt-0.5">
                                marked{b.no_show_marked_by ? ` by ${b.no_show_marked_by}` : ''}
                                {b.no_show_at ? ` · ${dt(b.no_show_at)}` : ''}
                              </div>
                            )}
                            {b.hold_extra_minutes > 0 && (
                              <div className="text-[10px] text-sky-600 mt-0.5">
                                +{b.hold_extra_minutes} min hold granted
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            {b.payment_status && b.payment_status !== 'NONE'
                              ? `${b.payment_status}${b.payment_amount ? ` · ${money(b.payment_amount)}` : ''}`
                              : '—'}
                          </td>
                          <td className="py-2 font-mono text-xs text-gray-500">
                            {b.order_number || b.reservation_code || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Order history */}
            <Card className="p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Order history</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="text-left font-medium py-2 pr-4">Order</th>
                      <th className="text-left font-medium py-2 pr-4">Date</th>
                      <th className="text-left font-medium py-2 pr-4">Location</th>
                      <th className="text-left font-medium py-2 pr-4">Status</th>
                      <th className="text-right font-medium py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {profile.orders.map((o) => (
                      <tr key={o.id}>
                        <td className="py-2 pr-4 font-medium text-gray-900">{o.order_number}</td>
                        <td className="py-2 pr-4">{dt(o.created_at)}</td>
                        <td className="py-2 pr-4">{o.location || '—'}</td>
                        <td className="py-2 pr-4">{o.status}</td>
                        <td className="py-2 text-right">{money(o.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
